import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Calendar, MessageSquare, Shield, Zap, Settings, X, Star, Clock, Target, Heart, Zap as ZapIcon, BookOpen, TrendingUp, UserCheck, Play } from 'lucide-react';
import LoginModal from './auth/LoginModal';
import { Button, Card, Badge } from './design-system';
import analyticsService from '../services/analyticsService';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const pageLoadTime = useRef(Date.now());
  const scrollDepthsTracked = useRef(new Set());
  const sectionsViewed = useRef(new Set());

  // Track landing page view on mount
  useEffect(() => {
    const trackPageView = async () => {
      // Wait a bit for analytics to initialize
      const waitForAnalytics = (retries = 10) => {
        if (analyticsService.isInitialized || retries === 0) {
          try {
            // Get URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const referrer = document.referrer || 'direct';
            const isFirstVisit = !localStorage.getItem('theraengage_visited');
            
            if (isFirstVisit) {
              localStorage.setItem('theraengage_visited', 'true');
            }

            // Detect device type
            const deviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';

            analyticsService.track('landing_page_viewed', {
              referrer: referrer,
              utm_source: urlParams.get('utm_source') || null,
              utm_medium: urlParams.get('utm_medium') || null,
              utm_campaign: urlParams.get('utm_campaign') || null,
              deviceType: deviceType,
              isFirstVisit: isFirstVisit
            });
          } catch (error) {
            console.error('Analytics tracking error:', error);
          }
        } else {
          setTimeout(() => waitForAnalytics(retries - 1), 100);
        }
      };
      
      waitForAnalytics();
    };

    trackPageView();
  }, []);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      // Check if analytics is ready
      if (!analyticsService.isInitialized) {
        return; // Skip if analytics not ready yet
      }

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      // Calculate scroll depth percentage
      const maxScroll = documentHeight - windowHeight;
      if (maxScroll <= 0) return; // Prevent division by zero
      
      const scrollDepth = Math.round((scrollTop / maxScroll) * 100);
      
      // Track at 25%, 50%, 75%, 100%
      const milestones = [25, 50, 75, 100];
      milestones.forEach(milestone => {
        if (scrollDepth >= milestone && !scrollDepthsTracked.current.has(milestone)) {
          scrollDepthsTracked.current.add(milestone);
          const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
          
          try {
            console.log(`📊 Tracking scroll depth: ${milestone}% at ${timeOnPage}s`); // Debug log
            analyticsService.track('landing_page_scrolled', {
              scrollDepth: milestone,
              timeOnPage: timeOnPage
            });
          } catch (error) {
            console.error('Analytics tracking error:', error);
          }
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add initial check on mount
    setTimeout(() => handleScroll(), 500);

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  // Track section views using Intersection Observer
  useEffect(() => {
    // Wait a bit for DOM to be ready
    const setupObserver = () => {
      const sections = [
        { id: 'hero', name: 'hero' },
        { id: 'demo-section', name: 'demo' },
        { id: 'problem-section', name: 'why' },
        { id: 'solution-section', name: 'value' },
        { id: 'credibility-section', name: 'features' },
        { id: 'outcomes-section', name: 'trust' },
        { id: 'cta-section', name: 'cta' }
      ];

      const observer = new IntersectionObserver(
        (entries) => {
          // Check if analytics is ready
          if (!analyticsService.isInitialized) {
            return; // Skip if analytics not ready yet
          }

          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const sectionName = entry.target.dataset.sectionName;
              if (sectionName && !sectionsViewed.current.has(sectionName)) {
                sectionsViewed.current.add(sectionName);
                const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                const scrollDepth = maxScroll > 0 
                  ? Math.round((window.scrollY / maxScroll) * 100)
                  : 0;

                try {
                  console.log(`📊 Tracking section view: ${sectionName} at ${timeOnPage}s`); // Debug log
                  analyticsService.track('landing_section_viewed', {
                    sectionName: sectionName,
                    timeOnPage: timeOnPage,
                    scrollDepth: scrollDepth
                  });
                } catch (error) {
                  console.error('Analytics tracking error:', error);
                }
              }
            }
          });
        },
        { threshold: 0.3 } // Trigger when 30% of section is visible
      );

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          element.dataset.sectionName = section.name;
          observer.observe(element);
        } else {
          console.warn(`Section element not found: ${section.id}`);
        }
      });

      return observer;
    };

    // Wait for DOM to be ready
    let observerInstance = null;
    const timeoutId = setTimeout(() => {
      observerInstance = setupObserver();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerInstance) {
        observerInstance.disconnect();
      }
    };
  }, []);

  const trackCTAClick = (buttonText, buttonLocation) => {
    const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
    try {
      analyticsService.track('landing_page_cta_clicked', {
        buttonText: buttonText,
        buttonLocation: buttonLocation,
        timeOnPage: timeOnPage
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };

  const handleGetStarted = () => {
    setIsLoginModalOpen(true);
  };

  const handleTryItFree = (location = 'hero') => {
    trackCTAClick('Try it for Free', location);
    setIsLoginModalOpen(true);
    // Modal open tracking happens via onModalOpen callback
  };

  const handleSeeHowItWorks = () => {
    trackCTAClick('Watch Demo', 'hero');
    // Scroll to the demo section
    document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLoginModalOpen = (trigger) => {
    const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
    try {
      analyticsService.track('login_modal_opened', {
        trigger: trigger,
        timeOnPage: timeOnPage
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Navigation */}
      <nav className="shadow-sm border-b fixed top-0 left-0 right-0 z-50 md:relative md:top-auto md:left-auto md:right-auto md:z-auto" style={{ backgroundColor: 'white', borderColor: 'rgba(0, 0, 0, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center" style={{ height: '64px' }}>
            {/* Logo */}
            <div className="flex items-center">
              <div className="text-xl md:text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>TheraEngage</div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="outline"
                size="medium"
                onClick={() => {
                  trackCTAClick('Sign In', 'nav_desktop');
                  handleGetStarted();
                }}
              >
                Sign In
              </Button>
              <Button 
                variant="primary"
                size="medium"
                onClick={() => {
                  handleTryItFree('nav_desktop');
                }}
              >
                Try it free
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-md transition-colors"
                style={{ 
                  color: 'var(--color-text)',
                  backgroundColor: isMenuOpen ? 'rgba(44, 166, 164, 0.1)' : 'transparent'
                }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <div className="flex flex-col gap-3">
                <Button 
                  variant="outline"
                  size="medium"
                  onClick={() => {
                    trackCTAClick('Sign In', 'nav_mobile');
                    handleGetStarted();
                  }}
                  className="w-full"
                >
                  Sign In
                </Button>
                <Button 
                  variant="primary"
                  size="medium"
                  onClick={() => {
                    handleTryItFree('nav_mobile');
                  }}
                  className="w-full"
                >
                  Try it free
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 lg:py-12 pt-20 md:pt-6">
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
             {/* Left Side - Content */}
             <div className="space-y-8 md:space-y-10">
               <div className="space-y-6">
                 <h1 className="text-4xl md:text-5xl lg:text-6xl leading-tight max-w-2xl" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
                   Support your clients between sessions
                 </h1>
                  <p className="text-xl md:text-2xl leading-relaxed max-w-2xl" style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                    A therapist-guided platform for goals, mood check-ins, and grounding activities — helping clients stay connected to their healing journey.
                  </p>
               </div>

              <div className="flex flex-col sm:flex-row gap-4">
                 <Button 
                   variant="primary"
                   size="large"
                   onClick={handleTryItFree}
                   className="flex items-center justify-center gap-2"
                 >
                   <span>Try TheraEngage Free</span>
                   <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
                 </Button>
                <Button 
                  variant="outline"
                  size="large"
                  onClick={() => {
                    trackCTAClick('Try Interactive Demo', 'hero');
                    document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center justify-center gap-2"
                >
                  <Play className="h-4 w-4 md:h-5 md:w-5" />
                  <span>Try Interactive Demo</span>
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5" style={{ color: 'var(--color-success)' }} />
                  <span>Privacy-first</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5" style={{ color: 'var(--color-success)' }} />
                  <span>Designed with clinicians</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 md:h-5 md:w-5" style={{ color: 'var(--color-success)' }} />
                  <span>No busywork</span>
                </div>
              </div>
            </div>

            {/* Right Side - Hero Image */}
            <div className="relative order-first lg:order-last">
              <img 
                src="/images/landing/TheraEngage.gif?v=4.1" 
                alt="TheraEngage - AI-powered therapy platform" 
                className="w-full h-auto rounded-lg shadow-2xl"
                style={{ borderRadius: 'var(--radius-lg)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
              See TheraEngage in Action
            </h2>
            <p className="text-lg md:text-xl" style={{ color: 'var(--color-text)', opacity: 0.8 }}>
              Take an interactive tour of the platform
            </p>
          </div>
          <div style={{ 
            position: 'relative', 
            boxSizing: 'content-box', 
            maxHeight: '80svh', 
            width: '100%', 
            aspectRatio: '1.6415401301518437', 
            padding: '40px 0 40px 0' 
          }}>
            <iframe 
              src="https://app.supademo.com/embed/cmi0niy9400l4zj0hbty4mf7o?embed_v=2&utm_source=embed" 
              loading="lazy" 
              title="TheraEngage" 
              allow="clipboard-write" 
              frameBorder="0" 
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen={true}
              style={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '100%' 
              }}
            />
          </div>
        </div>
      </section>

       {/* The WHY Section */}
       <section id="problem-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'var(--color-background)' }}>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-12">
             <h2 className="text-3xl md:text-4xl mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
               Therapy works best when clients feel supported in the moments between sessions.
             </h2>
           </div>

           <div className="text-center max-w-4xl mx-auto">
             <Card variant="default" padding="large">
               <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: 'var(--color-text)' }}>
                 Clients often face emotional challenges or important insights outside of the therapy room.
               </p>
               <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                 But without gentle guidance or a place to reflect, they may lose momentum, forget tasks, or feel disconnected from the process.
               </p>
               <p className="text-lg md:text-xl leading-relaxed" style={{ color: 'var(--color-text)' }}>
                 <strong>TheraEngage bridges that gap — keeping care continuous, steady, and aligned with your therapeutic approach.</strong>
               </p>
             </Card>
           </div>
         </div>
       </section>

       {/* The VALUE FOR THERAPISTS Section */}
       <section id="solution-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'white' }}>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-12">
             <h2 className="text-3xl md:text-4xl mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
               Designed for clinical reliability and therapeutic impact.
             </h2>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
             <Card variant="default" padding="large" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)', borderColor: 'var(--color-primary)' }}>
               <div className="flex items-start gap-4">
                 <CheckCircle className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: 'var(--color-primary)' }} />
                 <div>
                   <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Better visibility into your client's week</h3>
                   <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                     Daily mood check-ins highlight patterns, emotional shifts, and potential talking points — helping you tailor sessions more effectively.
                   </p>
                 </div>
               </div>
             </Card>

             <Card variant="default" padding="large" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)', borderColor: 'var(--color-primary)' }}>
               <div className="flex items-start gap-4">
                 <CheckCircle className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: 'var(--color-primary)' }} />
                 <div>
                   <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Higher follow-through on therapeutic goals</h3>
                   <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                     Small, personalized steps help clients stay engaged with cognitive, behavioral, or mindfulness tasks you assign.
                   </p>
                 </div>
               </div>
             </Card>

             <Card variant="default" padding="large" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)', borderColor: 'var(--color-primary)' }}>
               <div className="flex items-start gap-4">
                 <CheckCircle className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: 'var(--color-primary)' }} />
                 <div>
                   <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Support clients without increasing your workload</h3>
                   <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                     TheraEngage automates reminders, organizes activities, and provides a gentle companion (Opie) to keep clients on track.
                   </p>
                 </div>
               </div>
             </Card>

             <Card variant="default" padding="large" style={{ backgroundColor: 'rgba(44, 166, 164, 0.1)', borderColor: 'var(--color-primary)' }}>
               <div className="flex items-start gap-4">
                 <CheckCircle className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: 'var(--color-primary)' }} />
                 <div>
                   <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Make each session more productive</h3>
                   <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                     You spend less time catching up and more time doing meaningful therapeutic work.
                   </p>
                 </div>
               </div>
             </Card>
           </div>
         </div>
       </section>

       {/* Features Section */}
       <section id="credibility-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'var(--color-background)' }}>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-12">
             <h2 className="text-3xl md:text-4xl mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
               Everything you need for meaningful between-session support
             </h2>
           </div>

           <div className="max-w-6xl mx-auto space-y-6">
             {/* First Row: 2 cards at 50% width each */}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <Card variant="default" padding="large">
                 <Target className="h-8 w-8 mb-4" style={{ color: 'var(--color-primary)' }} />
                 <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Personalized Goals & Activities</h3>
                 <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   Turn therapeutic goals into manageable steps clients can complete at their own pace.
                 </p>
             </Card>

               <Card variant="default" padding="large">
                 <Users className="h-8 w-8 mb-4" style={{ color: 'var(--color-primary)' }} />
                 <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Opie: The Encouraging Companion</h3>
                 <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   A gentle, friendly mascot that nudges clients positively without pressure or judgment.
                 </p>
             </Card>
           </div>

             {/* Second Row: 3 cards at equal width */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               <Card variant="default" padding="large">
                 <Heart className="h-8 w-8 mb-4" style={{ color: 'var(--color-primary)' }} />
                 <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Daily Mood Check-Ins</h3>
                 <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   Capture emotional trends that improve treatment planning and deepen therapeutic understanding.
                 </p>
               </Card>

               <Card variant="default" padding="large">
                 <Zap className="h-8 w-8 mb-4" style={{ color: 'var(--color-primary)' }} />
                 <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Evidence-Aligned Grounding Tools</h3>
                 <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   Quick calming exercises crafted and assigned by you — for moments clients need immediate support.
                 </p>
               </Card>

             <Card variant="default" padding="large">
                 <Shield className="h-8 w-8 mb-4" style={{ color: 'var(--color-primary)' }} />
                 <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--color-text)' }}>Secure, Private, Therapist-Led</h3>
                 <p style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   HIPAA-aligned structure, encryption, therapist controls, and data minimization ensure safety and trust.
               </p>
             </Card>
             </div>
           </div>
         </div>
       </section>

       {/* Therapist Trust Section */}
       <section id="outcomes-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'white' }}>
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-12">
             <h2 className="text-3xl md:text-4xl mb-6" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
               Trusted by practicing therapists
             </h2>
           </div>

           <div className="max-w-3xl mx-auto">
             <Card variant="default" padding="large" style={{ backgroundColor: 'rgba(44, 166, 164, 0.05)' }}>
               <div>
                 <p className="text-lg md:text-xl italic mb-4" style={{ color: 'var(--color-text)' }}>
                   "I like the mood survey, it eliminates recency bias when we asked patient about their week"
                 </p>
                 <p className="text-base font-medium" style={{ color: 'var(--color-text)', opacity: 0.8 }}>
                   — Licensed Therapist, CA
                 </p>
               </div>
             </Card>
           </div>
         </div>
       </section>

       {/* Call-to-Action Section */}
       <section id="cta-section" className="py-6 md:py-10 lg:py-12" style={{ backgroundColor: 'var(--color-primary)' }}>
         <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
           <h2 className="text-3xl md:text-4xl text-white mb-6" style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--weight-bold)' }}>
             Bring compassionate continuity to your practice.
           </h2>
           <Button 
             variant="accent"
             size="large"
             onClick={() => {
               handleTryItFree('cta_section');
             }}
             className="flex items-center justify-center gap-2 mx-auto mb-4"
           >
             <span>Start Free</span>
             <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
           </Button>
           <p className="text-sm md:text-base" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
             Built with therapists. Designed for clients. Backed by research.
           </p>
         </div>
       </section>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: 'var(--color-text)', color: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>TheraEngage</div>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Where Therapy Continues Between Sessions
            </p>
            <div className="flex justify-center gap-6 mb-8">
              <a href="/about" className="hover:text-white transition-colors" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>About Us</a>
              <a href="/terms" className="hover:text-white transition-colors" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Terms and Conditions</a>
            </div>
            <div className="pt-8" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>&copy; {new Date().getFullYear()} TheraEngage. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {isLoginModalOpen && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 50, 
          padding: 'var(--space-4)' 
        }}>
          <Card variant="elevated" padding="none" style={{ maxWidth: '448px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: 'var(--space-4) var(--space-6)', 
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)' 
            }}>
              <h2 style={{ 
                fontSize: 'var(--text-h2)', 
                fontWeight: 'var(--weight-semibold)', 
                color: 'var(--color-text)', 
                fontFamily: 'var(--font-heading)',
                margin: 0
              }}>
                Sign In to TheraEngage
              </h2>
              <button
                onClick={() => setIsLoginModalOpen(false)}
                style={{ 
                  padding: 'var(--space-1)', 
                  borderRadius: 'var(--radius-md)', 
                  transition: 'all var(--transition-fast)',
                  color: 'var(--color-text)', 
                  opacity: 0.6,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.6'}
              >
                <X style={{ height: '24px', width: '24px' }} />
              </button>
            </div>
            <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
              <LoginModal 
                onClose={() => setIsLoginModalOpen(false)}
                onModalOpen={handleLoginModalOpen}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LandingPage;